// Turns server responses into something an insurance operator can act on.
//
// The policy endpoints answer with short technical phrases ("Duplicate policy
// number") or, on the staging path, raw MySQL text in an `error` field. Both
// used to reach the user as "Something went wrong", which told them nothing
// about what to do next. This keeps the server's meaning and adds the action.

const KNOWN_MESSAGES = [
  {
    match: /duplicate (policy number|entry)/i,
    text:
      "This policy number is already saved in the system. " +
      "Open the existing policy to review it, or correct the policy number if it was read incorrectly.",
  },
  {
    match: /policy staging record not found/i,
    text:
      "This document is no longer available to confirm. " +
      "It may already have been saved. Check the policy list before uploading it again.",
  },
  {
    match: /cancelled policy could not be moved/i,
    text:
      "This policy could not be saved from the uploaded document. " +
      "Please try confirming again, or enter the details manually.",
  },
  {
    match: /no file uploaded/i,
    text: "No file was received. Please choose a file and try again.",
  },
  {
    match: /file_id is required/i,
    text:
      "This form has lost its link to the uploaded document. " +
      "Please upload the document again.",
  },
]

/**
 * Extracts the server's explanation from an axios error and rephrases the
 * cases we recognise. Falls back to the server's own wording, then to generic
 * copy, so a message is never swallowed.
 */
export const getPolicyErrorMessage = (error, fallback) => {
  const data = error?.response?.data

  // The staging endpoint reports the generic step that failed in `message`
  // ("Failed to move policy to master") and the real cause in `error`
  // (ER_DUP_ENTRY ...), so both are matched before either is preferred.
  const candidates = [
    data?.message,
    data?.error,
    typeof data === "string" ? data : "",
  ].filter(Boolean)

  for (const entry of KNOWN_MESSAGES) {
    if (candidates.some(text => entry.match.test(text))) return entry.text
  }

  // No response body at all means the request never completed.
  if (!candidates.length) {
    if (error?.message) {
      return "We could not reach the server. Please check your connection and try again."
    }
    return fallback
  }

  const raw = candidates[0]

  // A raw SQL error is never useful to a business user.
  if (/^[A-Z]+_[A-Z_]+/.test(raw) || /\bSQL\b|\bsyntax\b/i.test(raw)) {
    return fallback
  }

  return raw
}

export default getPolicyErrorMessage
